import type { DiscoveryControlPanelProps } from "@/components/discovery/discovery.types";
import DateRangePicker from "@/components/ui/date-range-picker";
import Tab from "@/components/ui/tabs/Tab";
import { useFeature } from "@/lib/feature-management";
import { cn } from "@/lib/utils";
import useAuth from "@/store/auth.slice";
import type { LicenseType } from "@/types/user/userInfo";

const BROADCAST_LICENSE: LicenseType = "BROADCAST";
const PODCAST_LICENSE: LicenseType = "PODCAST";

const BASE_DISCOVERY_TABS = [
  { value: "clusters", label: "Narrative Clusters" },
  { value: "articles", label: "Articles" },
] as const;

const BROADCAST_TAB = { value: "broadcast", label: "Broadcast" } as const;
const PODCAST_TAB = { value: "podcast", label: "Podcast" } as const;

/**
 * Control panel for the Discovery view: date range and optional tab strip when `tabsBasePath` is set.
 * Article search lives in tab content (e.g. Articles tab), not in this header panel.
 */
const DiscoveryViewControlPanel = ({
  filterDays,
  isCustomDaysRange,
  startDate,
  endDate,
  onDateRangeChange,
  tabsBasePath,
}: DiscoveryControlPanelProps) => {
  const hasTabs = Boolean(tabsBasePath);
  const enableBroadcastClipsTab = useFeature(
    "enable-broadcast-clips-tab",
    false,
  );
  const hasBroadcastLicense = useAuth(
    (state) =>
      state.user.default_tenant?.active_license_types?.includes(
        BROADCAST_LICENSE,
      ) ?? false,
  );
  const enablePodcastEpisodesTab = useFeature(
    "enable-podcast-episodes-tab",
    false,
  );
  const hasPodcastLicense = useAuth(
    (state) =>
      state.user.default_tenant?.active_license_types?.includes(
        PODCAST_LICENSE,
      ) ?? false,
  );

  const tabs = [
    ...BASE_DISCOVERY_TABS,
    ...(enableBroadcastClipsTab && hasBroadcastLicense ? [BROADCAST_TAB] : []),
    ...(enablePodcastEpisodesTab && hasPodcastLicense ? [PODCAST_TAB] : []),
  ];

  const datePicker = (
    <div className="col-start-2 row-start-1 flex justify-end shrink-0">
      <DateRangePicker
        filterDays={filterDays}
        isCustomDaysRange={isCustomDaysRange}
        startDate={startDate}
        endDate={endDate}
        onApply={onDateRangeChange}
      />
    </div>
  );

  return (
    <div
      className={cn(
        "sticky top-0 z-10 w-full overflow-x-auto overflow-y-visible pt-4 bg-transparent px-8 py-4",
        hasTabs
          ? "grid grid-cols-2 grid-rows-2 items-start pt-4 pb-0"
          : "grid grid-cols-[1fr_auto] items-start gap-4",
      )}
    >
      {hasTabs && tabsBasePath && (
        <div className="col-start-1 row-start-2">
          <Tab
            tabs={tabs}
            basePath={tabsBasePath}
            aria-label="Discovery tabs"
          />
        </div>
      )}
      {datePicker}
    </div>
  );
};

export default DiscoveryViewControlPanel;
